import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const initialState = {
    isTradeIndividualOrderLoading: false,
    isInsertOrderLoading: false,
    isUpdateOrderLoading: false,
    isRemoveOrderLoading: false,
    isGetTradingLimitsLoading: false,
    isSaveTradingLimitsLoading: false,
    isGetEexUserLimitsLoading: false,
    isSaveEexUserLimitsLoading: false,
    isGetAutoPricingLimitsLoading: false,
    isSaveAutoPricingLimitsLoading: false,
    isAuthError: false
};

const isAuthError = (error) => {
    const status = error?.status || error?.response?.status;
    if (status === 401 || status === 403) return true;
    const msg = (
        error?.message ||
        error?.data?.message ||
        error?.data?.error ||
        error?.data?.detail ||
        ''
    ).toLowerCase();
    return (
        msg.includes('unauthorized') ||
        msg.includes('invalid token') ||
        msg.includes('token expired') ||
        msg.includes('token is invalid') ||
        msg.includes('authentication') ||
        msg.includes('not authenticated') ||
        msg.includes('missing required parameters: username and token')
    );
};

const getCredentials = () => {
    const token = localStorage.getItem('eex_token');
    const username = localStorage.getItem('eex_username');
    return { token, username };
};

export const apiTradeIndividualOrder = createAsyncThunk(
    'eex/tradeIndividualOrder',
    async (payload, thunkAPI) => {
        try {
            const { token, username } = getCredentials();
            const response = await axios.post(
                `/eexapi/trade_individual_order?username=${username}&token=${token}`,
                { ...payload, token, username },
                {
                    headers: {
                        Authorization: token ? `${token}` : ''
                    }
                }
            );
            return {
                data: response.data,
                status: response.status,
                statusText: response.statusText
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response.data);
        }
    }
);

export const apiInsertOrder = createAsyncThunk(
    'eex/insertOrder',
    async (payload, thunkAPI) => {
        try {
            const { token, username } = getCredentials();
            const response = await axios.post(
                `/eexapi/insert_order?username=${username}&token=${token}`,
                { ...payload, token, username },
                {
                    headers: {
                        Authorization: token ? `${token}` : ''
                    }
                }
            );
            return {
                data: response.data,
                status: response.status,
                statusText: response.statusText
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response.data);
        }
    }
);

export const apiUpdateOrder = createAsyncThunk(
    'eex/updateOrder',
    async (payload, thunkAPI) => {
        try {
            const { token, username } = getCredentials();
            const response = await axios.post(
                `/eexapi/update_order?username=${username}&token=${token}`,
                { ...payload, token, username },
                {
                    headers: {
                        Authorization: token ? `${token}` : ''
                    }
                }
            );
            return {
                data: response.data,
                status: response.status,
                statusText: response.statusText
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response.data);
        }
    }
);

export const apiRemoveOrder = createAsyncThunk(
    'eex/removeOrder',
    async (payload, thunkAPI) => {
        try {
            const { token, username } = getCredentials();
            const response = await axios.post(
                `/eexapi/remove_order?username=${username}&token=${token}`,
                { ...payload, token, username },
                {
                    headers: {
                        Authorization: token ? `${token}` : ''
                    }
                }
            );
            return {
                data: response.data,
                status: response.status,
                statusText: response.statusText
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response.data);
        }
    }
);

export const apiGetTradingLimits = createAsyncThunk(
    'eex/getTradingLimits',
    async (_, thunkAPI) => {
        try {
            const response = await axios.get('/api/limits/set_eex_limits');
            return {
                data: response.data,
                status: response.status,
                statusText: response.statusText
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(
                error?.response?.data || { message: error.message }
            );
        }
    }
);

export const apiSaveTradingLimits = createAsyncThunk(
    'eex/saveTradingLimits',
    async (payload, thunkAPI) => {
        try {
            console.log("Saving trading limits with payload:", payload);
            const response = await axios.post(
                '/api/limits/set_eex_limits',
                payload
            );
            return {
                data: response.data,
                status: response.status,
                statusText: response.statusText
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(
                error?.response?.data || { message: error.message }
            );
        }
    }
);

export const apiGetEexUserLimits = createAsyncThunk(
    'eex/getEexUserLimits',
    async (_, thunkAPI) => {
        try {
            const response = await axios.get('/api/limits/client_limits');
            return {
                data: response.data,
                status: response.status,
                statusText: response.statusText
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(
                error?.response?.data || { message: error.message }
            );
        }
    }
);

export const apiSaveEexUserLimits = createAsyncThunk(
    'eex/saveEexUserLimits',
    async (payload, thunkAPI) => {
        try {
            console.log("Saving EEX user limits with payload:", payload);
            const response = await axios.post(
                '/api/limits/client_limits',
                payload
            );
            return {
                data: response.data,
                status: response.status,
                statusText: response.statusText
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(
                error?.response?.data || { message: error.message }
            );
        }
    }
);

export const apiGetAutoPricingLimits = createAsyncThunk(
    'eex/getAutoPricingLimits',
    async (_, thunkAPI) => {
        try {
            const response = await axios.get('/eexapi/get_auto_pricing_limits');
            return {
                data: response.data,
                status: response.status,
                statusText: response.statusText
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(
                error?.response?.data || { message: error.message }
            );
        }
    }
);

export const apiSaveAutoPricingLimits = createAsyncThunk(
    'eex/saveAutoPricingLimits',
    async (payload, thunkAPI) => {
        try {
            const response = await axios.post(
                '/eexapi/save_auto_pricing_limits',
                payload
            );
            return {
                data: response.data,
                status: response.status,
                statusText: response.statusText
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(
                error?.response?.data || { message: error.message }
            );
        }
    }
);

export const apiLoginEex = createAsyncThunk(
    'eex/loginEex',
    async (payload, thunkAPI) => {
        try {
            const response = await axios.post('/eexapi/login', payload);
            return {
                data: response.data,
                status: response.status,
                statusText: response.statusText
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(
                error?.response?.data || { message: error.message }
            );
        }
    }
);

export const eexSlice = createSlice({
    name: 'eex',
    initialState,
    reducers: {
        clearAuthError: (state) => {
            state.isAuthError = false;
        }
    },
    extraReducers: (builder) => {
        // Trade Individual Order
        builder
            .addCase(apiTradeIndividualOrder.pending, (state) => {
                state.isTradeIndividualOrderLoading = true;
            })
            .addCase(apiTradeIndividualOrder.fulfilled, (state) => {
                state.isTradeIndividualOrderLoading = false;
            })
            .addCase(apiTradeIndividualOrder.rejected, (state, action) => {
                state.isTradeIndividualOrderLoading = false;
                if (isAuthError(action.payload || action.error)) {
                    state.isAuthError = true;
                }
            });

        // Insert Order
        builder
            .addCase(apiInsertOrder.pending, (state) => {
                state.isInsertOrderLoading = true;
            })
            .addCase(apiInsertOrder.fulfilled, (state) => {
                state.isInsertOrderLoading = false;
            })
            .addCase(apiInsertOrder.rejected, (state, action) => {
                state.isInsertOrderLoading = false;
                if (isAuthError(action.payload || action.error)) {
                    state.isAuthError = true;
                }
            });

        // Update Order
        builder
            .addCase(apiUpdateOrder.pending, (state) => {
                state.isUpdateOrderLoading = true;
            })
            .addCase(apiUpdateOrder.fulfilled, (state) => {
                state.isUpdateOrderLoading = false;
            })
            .addCase(apiUpdateOrder.rejected, (state, action) => {
                state.isUpdateOrderLoading = false;
                if (isAuthError(action.payload || action.error)) {
                    state.isAuthError = true;
                }
            });

        // Remove Order
        builder
            .addCase(apiRemoveOrder.pending, (state) => {
                state.isRemoveOrderLoading = true;
            })
            .addCase(apiRemoveOrder.fulfilled, (state) => {
                state.isRemoveOrderLoading = false;
            })
            .addCase(apiRemoveOrder.rejected, (state, action) => {
                state.isRemoveOrderLoading = false;
                if (isAuthError(action.payload || action.error)) {
                    state.isAuthError = true;
                }
            });

        // Get Trading Limits
        builder
            .addCase(apiGetTradingLimits.pending, (state) => {
                state.isGetTradingLimitsLoading = true;
            })
            .addCase(apiGetTradingLimits.fulfilled, (state) => {
                state.isGetTradingLimitsLoading = false;
            })
            .addCase(apiGetTradingLimits.rejected, (state, action) => {
                state.isGetTradingLimitsLoading = false;
                if (isAuthError(action.payload || action.error)) {
                    state.isAuthError = true;
                }
            });

        // Save Trading Limits
        builder
            .addCase(apiSaveTradingLimits.pending, (state) => {
                state.isSaveTradingLimitsLoading = true;
            })
            .addCase(apiSaveTradingLimits.fulfilled, (state) => {
                state.isSaveTradingLimitsLoading = false;
            })
            .addCase(apiSaveTradingLimits.rejected, (state, action) => {
                state.isSaveTradingLimitsLoading = false;
                if (isAuthError(action.payload || action.error)) {
                    state.isAuthError = true;
                }
            });

        // Get EEX User Limits
        builder
            .addCase(apiGetEexUserLimits.pending, (state) => {
                state.isGetEexUserLimitsLoading = true;
            })
            .addCase(apiGetEexUserLimits.fulfilled, (state) => {
                state.isGetEexUserLimitsLoading = false;
            })
            .addCase(apiGetEexUserLimits.rejected, (state, action) => {
                state.isGetEexUserLimitsLoading = false;
                if (isAuthError(action.payload || action.error)) {
                    state.isAuthError = true;
                }
            });

        // Save EEX User Limits
        builder
            .addCase(apiSaveEexUserLimits.pending, (state) => {
                state.isSaveEexUserLimitsLoading = true;
            })
            .addCase(apiSaveEexUserLimits.fulfilled, (state) => {
                state.isSaveEexUserLimitsLoading = false;
            })
            .addCase(apiSaveEexUserLimits.rejected, (state, action) => {
                state.isSaveEexUserLimitsLoading = false;
                if (isAuthError(action.payload || action.error)) {
                    state.isAuthError = true;
                }
            });

        // Get Auto Pricing Limits
        builder
            .addCase(apiGetAutoPricingLimits.pending, (state) => {
                state.isGetAutoPricingLimitsLoading = true;
            })
            .addCase(apiGetAutoPricingLimits.fulfilled, (state) => {
                state.isGetAutoPricingLimitsLoading = false;
            })
            .addCase(apiGetAutoPricingLimits.rejected, (state, action) => {
                state.isGetAutoPricingLimitsLoading = false;
                if (isAuthError(action.payload || action.error)) {
                    state.isAuthError = true;
                }
            });

        // Save Auto Pricing Limits
        builder
            .addCase(apiSaveAutoPricingLimits.pending, (state) => {
                state.isSaveAutoPricingLimitsLoading = true;
            })
            .addCase(apiSaveAutoPricingLimits.fulfilled, (state) => {
                state.isSaveAutoPricingLimitsLoading = false;
            })
            .addCase(apiSaveAutoPricingLimits.rejected, (state, action) => {
                state.isSaveAutoPricingLimitsLoading = false;
                if (isAuthError(action.payload || action.error)) {
                    state.isAuthError = true;
                }
            });

        // Login EEX
        builder
            .addCase(apiLoginEex.pending, (state) => {
                // You can add a loading state for login if needed
            })
            .addCase(apiLoginEex.fulfilled, (state) => {
                // Handle successful login if needed
            })
            .addCase(apiLoginEex.rejected, (state) => {
                // Handle login failure if needed
            });
    }
});

export const { clearAuthError } = eexSlice.actions;
export default eexSlice.reducer;
